import React, { CSSProperties } from "react";

import Utilities from "../../foundation/Utility";
import PageSpecification from "../../types/system/PageSpecification";
import GridBody from "../../components/grid/GridBody";
import GridColumn from "../../components/grid/GridColumn";
import GridHeader from "../../components/grid/GridHeader";
import { AppContext } from "../../types/system/AppContext";
import GridPager from "./GridPager";
import { createPortal } from "react-dom";
import GridGrouping from "./GridGrouping";

import "./Grid.scss";

export type GridElements<T, U> =
    | React.ReactElement<GridColumn<U>>
    | React.ReactElement<GridGrouping<T, U>>;

interface GridProps<T, U = T> {
    className?: string;
    defaultSortField?: string;
    draggable?: boolean;
    droppable?: boolean;
    fixedHeader?: boolean;
    fullHeight?: boolean;
    fullWidth?: boolean;
    globalSelect?: boolean;
    globalFilter?: boolean;
    keyField: string;
    limitTo?: number;
    height?: string;
    maxHeight?: string;
    pageSpecification?: PageSpecification;
    rounded?: boolean;
    selectedRecords?: U[];
    singleSelect?: boolean;
    squared?: boolean;
    style?: CSSProperties | undefined;
    value?: T[];
    window?: boolean;
    collapsed?: boolean;
    bulkEdit?: boolean;
    blockSort?: boolean;
    paged?: boolean;

    children?:
        | React.ReactElement<GridColumn<U>>
        | React.ReactElement<GridColumn<U>>[]
        | React.ReactElement<GridGrouping<T, U>>;

    valueEdited?: (record: U, changes: U) => void;
    selectionChanged?: (
        record: T | U,
        selection: (T | U)[],
        column: GridColumn<T | U>,
    ) => void;
    columnChanged?: (column: GridColumn<T | U>) => void;
    cellClicked?: (record: T | U, column: GridColumn<T | U>) => void;
    cellDoubleClicked?: (record: T | U, column: GridColumn<T | U>) => void;
    gridDragDropped?: (records: (T | U)[]) => void;
    onPageChange?: (specification: PageSpecification) => void;
}

interface GridState<T, U = T> {
    anchorRecord: T | U;
    ctrlDown: boolean;
    dragContainerRef?: React.RefObject<HTMLTableSectionElement>;
    dragStartOffsets?: { x: number; y: number };
    dragContainerDims?: { x: number; y: number; width: number };
    dragOver: boolean;
    filters: Map<string, any>;
    contRef: React.RefObject<HTMLDivElement>;
    gridRef: React.RefObject<HTMLTableElement>;
    pageSpecification?: PageSpecification;
    selectedColumn: GridColumn<U>;
    selectedRecord: T | U;
    selectedRecords: (T | U)[];
    selectionStart: T | U;
    shiftDown: boolean;
    sortField: string;
    bulkAll: boolean;
    visible: T[];
}

class Grid<T, U = T> extends React.Component<GridProps<T, U>, GridState<T, U>> {
    static contextType = AppContext;
    context!: React.ContextType<typeof AppContext>;

    constructor(props: GridProps<T, U>) {
        super(props);

        this.state = {
            anchorRecord: null as U,
            ctrlDown: false,
            dragOver: false,
            filters: new Map<string, any>(),
            contRef: React.createRef<HTMLDivElement>(),
            gridRef: React.createRef<HTMLTableElement>(),
            selectedColumn: null as GridColumn<U>,
            selectedRecord: null as U,
            selectedRecords: [],
            selectionStart: null as U,
            shiftDown: false,
            sortField: props.defaultSortField,
            visible: [],
            pageSpecification: props.paged
                ? ({
                      page: 0,
                      size: 20,
                      total: props.value?.length ?? 0,
                  } as PageSpecification)
                : undefined,
            bulkAll: false,
        };
    }

    componentDidMount(): void {
        if (this.props.droppable)
            this.context.registerDropGrid(this.dragOverBound);
        window.addEventListener("focus", this.boundWindowFocus);

        this.setState(() => ({
            visible: this.prepareVisible(
                this.state.sortField,
                this.state.filters,
            ),
        }));
    }
    componentWillUnmount(): void {
        this.context.deregisterDropGrid(this.dragOverBound);
        window.removeEventListener("focus", this.boundWindowFocus);
    }

    componentDidUpdate(
        prevProps: Readonly<GridProps<T, U>>,
        prevState: Readonly<GridState<T, U>>,
        snapshot?: any,
    ): void {
        if (
            this.props.selectedRecords &&
            JSON.stringify(prevProps.selectedRecords) !==
                JSON.stringify(this.props.selectedRecords)
        )
            this.setState(() => ({
                selectedRecords: this.props.selectedRecords,
            }));

        if (prevProps.value !== this.props.value)
            this.setState(prevState => ({
                pageSpecification: !!prevState.pageSpecification
                    ? ({
                          ...prevState.pageSpecification,
                          total: this.props.value?.length ?? 0,
                      } as PageSpecification)
                    : undefined,
            }));

        if (
            prevProps.value !== this.props.value ||
            prevState.sortField !== this.state.sortField ||
            prevProps.limitTo !== this.props.limitTo ||
            prevState.pageSpecification?.page !== this.state.pageSpecification?.page ||
            prevState.pageSpecification?.size !== this.state.pageSpecification?.size
        )
            this.setState(prevState => ({
                visible: this.prepareVisible(
                    this.state.sortField,
                    this.state.filters,
                ),
            }));
    }

    windowFocus(event: MouseEvent) {
        this.setState(() => ({
            shiftDown: event.shiftKey,
            ctrlDown: event.ctrlKey || event.metaKey,
        }));
    }
    boundWindowFocus = this.windowFocus.bind(this);

    prepareVisible(sortField?: string, filters?: Map<string, any>): T[] {
        let visible = this.props.value ?? [];
        if (this.props.limitTo)
            visible = visible.slice(undefined, this.props.limitTo);
        else visible = visible.slice();
        if (sortField) visible = visible.sort(Utilities.getSorter([sortField]));
        if (!this.getGrouping() && filters && filters.size > 0) {
            const filterer = Utilities.getFilterer(filters);
            visible = visible.filter(filterer);
        }

        if (this.state?.pageSpecification) {
            visible = visible.slice(
                this.state.pageSpecification.page *
                    this.state.pageSpecification.size,
                (this.state.pageSpecification.page + 1) *
                    this.state.pageSpecification.size,
            );
        }

        return visible;
    }

    getGrouping(): GridGrouping<T, U> | undefined {
        return (
            (this.props.children instanceof Array
                ? this.props.children
                : [this.props.children]) as GridElements<T, U>[]
        )
            .filter(entry => entry.type === GridGrouping)
            .map(entry => entry.props as GridGrouping<T, U>)[0];
    }

    getColumns(): GridColumn<U>[] {
        return (
            (this.props.children instanceof Array
                ? this.props.children
                : [this.props.children]) as GridElements<T, U>[]
        )
            .filter(entry => entry.type === GridColumn)
            .map(entry => entry.props as GridColumn<U>);
    }

    clearFilters() {
        this.setState(() => ({
            filters: new Map<string, any>(),
            visible: this.prepareVisible(
                this.state.sortField,
                new Map<string, any>(),
            ),
        }));
    }

    filterChanged(field: string, value: any) {
        const column = this.getColumns().find(col => col.field === field);
        if (!column) return;

        const filter = this.state.filters;
        Utilities.isNull(value)
            ? filter.delete(field)
            : filter.set(field, value);

        this.setState(() => ({
            filters: filter,
            visible: this.prepareVisible(this.state.sortField, filter),
        }));
    }

    clickHeader(column: GridColumn<U>) {
        let newSort = "";
        if (this.state.sortField === column.field) newSort = `-${column.field}`;
        else if (this.state.sortField === this.props.defaultSortField)
            newSort = column.field;
        else newSort = this.props.defaultSortField;

        this.setState(() => ({ sortField: newSort }));
    }
    focusCell(record: U, column: GridColumn<U>) {
        this.selectCell(record, this.groupProcess(record), column);
    }
    checkRow(record: U, value: boolean) {
        const selection = (this.state.selectedRecords ?? []).slice();

        if (!value && this.state.selectedRecord === record)
            this.setState(() => ({
                selectedRecord: null,
                selectedColumn: null,
            }));

        if (value) selection.push(record);
        else selection.remove(record);

        this.props?.selectionChanged(record, selection, null);

        this.setState(() => ({
            selectedRecords: selection.distinct(),
        }));
    }
    clickCell(record: U, column: GridColumn<U>) {
        this.selectCell(record, this.groupProcess(record), column);

        if (this.props.cellClicked) this.props.cellClicked(record, column);
    }
    doubleClickCell(record: U, column: GridColumn<U>) {
        this.selectCell(record, this.groupProcess(record), column);
        if (this.props.cellDoubleClicked)
            this.props.cellDoubleClicked(record, column);
    }
    groupProcess(record: U): U[] {
        let selection = [];
        if (!this.props.singleSelect && this.state.shiftDown)
            selection = this.groupSelectShift(record);
        else if (!this.props.singleSelect && this.state.ctrlDown)
            selection = this.groupSelectCtrl(record);
        else selection = this.groupSelectReset(record);

        return selection;
    }
    groupSelectShift(record: T | U): (T | U)[] {
        let startIndex = this.state.visible.indexOf(
            this.state.visible.find(
                item =>
                    item[this.props.keyField] ===
                    this.state.selectionStart[this.props.keyField],
            ),
        );
        let endIndex = this.state.visible.indexOf(
            this.state.visible.find(
                item =>
                    item[this.props.keyField] === record[this.props.keyField],
            ),
        );

        if (startIndex > endIndex) {
            const temp = startIndex;
            startIndex = endIndex;
            endIndex = temp;
        }

        let result = this.state.visible.filter(
            (_, index) => index >= startIndex && index <= endIndex,
        );

        this.setState(() => ({ selectedRecords: result }));

        return result;
    }
    groupSelectCtrl(record: T | U): (T | U)[] {
        const records = [...this.state.selectedRecords];
        let index = records.indexOf(
            records.find(
                item =>
                    item[this.props.keyField] === record[this.props.keyField],
            ),
        );
        if (index >= 0) records.splice(index, 1);
        else records.push(record);

        this.setState(() => ({
            selectionStart: record,
            selectedRecords: records,
        }));

        return records;
    }
    groupSelectReset(record: T | U): (T | U)[] {
        this.setState(() => ({
            selectionStart: record,
            selectedRecords: [record],
        }));

        return [record];
    }
    selectCell(record: T | U, selection: (T | U)[], column: GridColumn<T | U>) {
        if (this.props.globalSelect || column.selectable || column.editable) {
            const oldColumn = this.state.selectedColumn;

            this.setState(() => ({
                selectedRecord: record,
                selectedColumn: column,
            }));

            if (selection && this.props?.selectionChanged)
                this.props?.selectionChanged(record, selection, column);

            if (column && (!oldColumn || oldColumn.field !== column.field))
                this.props.columnChanged?.(column);

            if (!selection) selection = [record];

            this.setState(() => ({
                selectedRecords: selection,
            }));
        }
    }
    movePrevRecord() {
        if (this.state.selectedRecord && this.state.selectedColumn) {
            let selectedIndex =
                this.state.visible.indexOf(
                    this.state.visible.find(
                        record =>
                            record[this.props.keyField] ===
                            this.state.selectedRecord[this.props.keyField],
                    ),
                ) - 1;
            selectedIndex = selectedIndex > 0 ? selectedIndex : 0;
            this.selectCell(
                this.state.visible[selectedIndex],
                null,
                this.state.selectedColumn,
            );
        }
    }
    moveNextRecord() {
        if (this.state.selectedRecord && this.state.selectedColumn) {
            let selectedIndex =
                this.state.visible.indexOf(
                    this.state.visible.find(
                        record =>
                            record[this.props.keyField] ===
                            this.state.selectedRecord[this.props.keyField],
                    ),
                ) + 1;
            selectedIndex =
                selectedIndex < this.state.visible.length
                    ? selectedIndex
                    : this.state.visible.length - 1;
            this.selectCell(
                this.state.visible[selectedIndex],
                null,
                this.state.selectedColumn,
            );
        }
    }
    movePrevColumn() {
        if (this.state.selectedRecord && this.state.selectedColumn) {
            let columns = this.getColumns();
            let selectedIndex =
                columns.indexOf(
                    columns.find(
                        column =>
                            column.field ===
                            (this.state.selectedColumn?.field ?? ""),
                    ),
                ) - 1;

            while (
                selectedIndex >= 0 &&
                !columns[selectedIndex].selectable &&
                !columns[selectedIndex].editable
            )
                selectedIndex--;

            if (selectedIndex >= 0)
                this.selectCell(
                    this.state.selectedRecord,
                    null,
                    columns[selectedIndex],
                );
        }
    }
    moveNextColumn() {
        if (this.state.selectedRecord && this.state.selectedColumn) {
            let columns = this.getColumns();
            let selectedIndex =
                columns.indexOf(
                    columns.find(
                        column =>
                            column.field ===
                            (this.state.selectedColumn?.field ?? ""),
                    ),
                ) + 1;

            while (
                selectedIndex < columns.length &&
                !columns[selectedIndex].selectable &&
                !columns[selectedIndex].editable
            )
                selectedIndex++;

            if (selectedIndex < columns.length)
                this.selectCell(
                    this.state.selectedRecord,
                    null,
                    columns[selectedIndex],
                );
        }
    }
    keyDown(event: KeyboardEvent) {
        if (event.key === "Shift") this.setState(() => ({ shiftDown: true }));
        else if (event.key === "Ctrl" || event.key === "Meta")
            this.setState(() => ({ ctrlDown: true }));
    }
    keyUp(event: KeyboardEvent) {
        if (event.key === "Shift") this.setState(() => ({ shiftDown: false }));
        else if (event.key === "Ctrl" || event.key === "Meta")
            this.setState(() => ({ ctrlDown: false }));
    }

    dragStart(
        _record: U,
        _column: React.ReactElement<GridColumn<U>>,
        event: React.MouseEvent,
        cell: React.RefObject<HTMLTableCellElement>,
    ) {
        if (this.props.draggable) {
            const bounding = cell.current?.getBoundingClientRect();
            this.setState(() => ({
                dragContainerRef: React.createRef<HTMLTableSectionElement>(),
                dragStartOffsets: {
                    x: event.clientX - (bounding?.left ?? 0),
                    y: event.clientY - (bounding?.top ?? 0),
                },
                dragContainerDims: {
                    x: bounding?.left ?? 0,
                    y: bounding?.top ?? 0,
                    width: this.state.gridRef.current?.offsetWidth ?? 0,
                },
            }));
            window.addEventListener("mousemove", this.dragMoveBound);
            window.addEventListener("mouseup", this.dragEndBound);
            window.addEventListener("mouseout", this.dragEndBound);
            window.addEventListener("mouseleave", this.dragEndBound);
        }
    }

    dragMove(event: MouseEvent) {
        this.setState(() => ({
            dragContainerDims: {
                x: event.clientX - (this.state.dragStartOffsets?.x ?? 0),
                y: event.clientY - (this.state.dragStartOffsets?.y ?? 0),
                width: this.state.dragContainerDims?.width ?? 0,
            },
        }));
        this.context.evaluateDropGrids({
            x: event.clientX,
            y: event.clientY,
        });
    }
    dragMoveBound = this.dragMove.bind(this);

    dragEnd(event: MouseEvent) {
        if (
            event.type === "mouseout" &&
            (event.relatedTarget as HTMLElement).nodeName !== "HTML"
        )
            return;

        this.setState(() => ({
            dragContainerRef: undefined,
            dragContainerDims: undefined,
        }));
        window.removeEventListener("mousemove", this.dragMoveBound);
        window.removeEventListener("mouseup", this.dragEndBound);
        window.removeEventListener("mouseout", this.dragEndBound);
        window.removeEventListener("mouseleave", this.dragEndBound);
        this.context.evaluateDropGrids(
            {
                x: Number.MIN_VALUE,
                y: Number.MIN_VALUE,
            },
            this.state.selectedRecords,
        );
    }
    dragEndBound = this.dragEnd.bind(this);

    dragOver(position: { x: number; y: number }, data?: U[]) {
        if (this.props.droppable) {
            if (!!data && this.state.dragOver) {
                this.props?.gridDragDropped?.(data);
                this.setState(() => ({ dragOver: false }));
            } else {
                const bounds =
                    this.state.contRef.current?.getBoundingClientRect();
                if (
                    bounds &&
                    position.x >= bounds.left &&
                    position.x <= bounds.right &&
                    position.y >= bounds.top &&
                    position.y <= bounds.bottom
                ) {
                    this.setState(() => ({ dragOver: true }));
                } else {
                    this.setState(() => ({ dragOver: false }));
                }
            }
        }
    }
    dragOverBound = this.dragOver.bind(this);

    setFieldValue(record: U, column: GridColumn<U>, value: any) {
        if (this.props.valueEdited) {
            let changes = {} as U;
            changes[column.field] = value;
            this.props.valueEdited(record, changes);
        }
    }

    onSelectAllChanged(value: boolean) {
        if (value) {
            this.setState(() => ({
                selectedRecords: this.props.value?.slice() ?? [],
                bulkAll: true,
            }));
        } else {
            this.setState(() => ({
                selectedRecords: [],
                bulkAll: false,
            }));
        }
    }

    onPageChange(specification: PageSpecification) {
        if (this.props.paged) {
            this.setState(() => ({
                pageSpecification: specification,
                visible: this.prepareVisible(
                    this.state.sortField,
                    this.state.filters,
                ),
            }));
        } else {
            this.props.onPageChange?.(specification);
        }
    }

    render(): React.ReactNode {
        let classes = "display-grid";
        if (this.props.className) classes += " " + this.props.className;
        if (this.props.rounded) classes += " rounded";
        if (this.props.squared) classes += " squared";
        if (this.props.window) classes += " window";
        if (this.props.collapsed) classes += " collapsed";
        if (this.props.fullWidth) classes += " full";
        if (this.props.fullHeight) classes += " full-height";
        if (this.state?.pageSpecification || this.props.pageSpecification)
            classes += " paged";
        if (
            this.state?.pageSpecification?.waiting ||
            this.props.pageSpecification?.waiting
        )
            classes += " loading";
        if (this.props.fixedHeader) classes += " fixed-header";
        if (this.props.draggable) classes += " draggable";
        if (this.state.dragOver) classes += " drag-over";

        return (
            <>
                <div
                    ref={this.state.contRef}
                    className={classes}>
                    <div
                        className="display-grid-container"
                        style={{
                            maxHeight: this.props.maxHeight,
                            height: this.props.height,
                        }}>
                        <table
                            style={this.props.style}
                            ref={this.state.gridRef}>
                            <GridHeader
                                fixedHeader={
                                    this.props.fixedHeader || this.props.window
                                }
                                columns={this.getColumns()}
                                filters={this.state.filters}
                                sortField={this.state.sortField}
                                globalFilter={this.props.globalFilter ?? false}
                                onClick={this.clickHeader.bind(this)}
                                onClearFilters={this.clearFilters.bind(this)}
                                filterChanged={this.filterChanged.bind(this)}
                                bulkEdit={this.props.bulkEdit}
                                bulkAll={this.state.bulkAll}
                                blockSort={this.props.blockSort}
                                onSelectAllChanged={this.onSelectAllChanged.bind(
                                    this,
                                )}
                            />
                            <GridBody<T, U>
                                columns={this.getColumns()}
                                grouping={this.getGrouping()}
                                records={this.state.visible}
                                limitTo={this.props.limitTo}
                                globalSelect={this.props.globalSelect ?? false}
                                keyField={this.props.keyField}
                                sortField={this.state.sortField}
                                filters={this.state.filters}
                                draggable={this.props.draggable}
                                dragContainerRef={this.state.dragContainerRef}
                                selectedRecord={this.state.selectedRecord}
                                selectedRecords={this.state.selectedRecords}
                                selectedColumn={this.state.selectedColumn}
                                pageSpecification={
                                    this.state?.pageSpecification ??
                                    this.props.pageSpecification
                                }
                                onKeyDown={this.keyDown.bind(this)}
                                onKeyUp={this.keyUp.bind(this)}
                                onFocus={this.focusCell.bind(this)}
                                onBlur={() => {}}
                                onCheck={this.checkRow.bind(this)}
                                onClick={this.clickCell.bind(this)}
                                onDoubleClick={this.doubleClickCell.bind(this)}
                                onDragStart={this.dragStart.bind(this)}
                                valueEdited={this.setFieldValue.bind(this)}
                                prevRecordRequested={this.movePrevRecord.bind(
                                    this,
                                )}
                                nextRecordRequested={this.moveNextRecord.bind(
                                    this,
                                )}
                                prevColumnRequested={this.movePrevColumn.bind(
                                    this,
                                )}
                                nextColumnRequested={this.moveNextColumn.bind(
                                    this,
                                )}
                                bulkEdit={this.props.bulkEdit}
                            />
                        </table>
                    </div>
                    {(this.state?.pageSpecification ||
                        this.props.pageSpecification) && (
                        <GridPager
                            specification={
                                this.state?.pageSpecification ??
                                this.props.pageSpecification
                            }
                            onChange={this.onPageChange.bind(this)}
                        />
                    )}
                </div>

                {this.state.dragContainerRef &&
                    createPortal(
                        <table
                            className="display-grid-drag-container"
                            style={{
                                top: this.state.dragContainerDims?.y,
                                left: this.state.dragContainerDims?.x,
                                width: this.state.dragContainerDims?.width,
                            }}>
                            <tbody ref={this.state.dragContainerRef}></tbody>
                        </table>,
                        document.querySelector(".page-elements"),
                    )}
            </>
        );
    }
}

export default Grid;
